/**
 * Editor zum Anlegen und Bearbeiten eines Rezepts.
 */
import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client.ts';
import { WebImport, urlFromShared } from '../components/WebImport.tsx';
import type { RecipeDraft } from '../api/types.ts';
import { DifficultyBadge } from '../components/DifficultyBadge.tsx';
import { IngredientListEditor } from '../components/IngredientListEditor.tsx';
import { RecipeMediaEditor } from '../components/RecipeMediaEditor.tsx';
import { StepListEditor } from '../components/StepListEditor.tsx';
import { parseIngredientLines } from '../lib/ingredientParser.ts';
import { useCatalog } from '../state/catalog.ts';
import { useRecipeEditor } from '../state/useRecipeEditor.ts';
import { texts } from '../texts/de.ts';

/** Zeitfelder mit deutscher Beschriftung. */
const TIME_FIELDS: readonly { key: string; label: string }[] = [
  { key: 'preparationMinutes', label: texts.detail.preparation },
  { key: 'activeMinutes', label: texts.detail.active },
  { key: 'cookingMinutes', label: texts.detail.cooking },
  { key: 'bakingMinutes', label: texts.detail.baking },
  { key: 'restingMinutes', label: texts.detail.resting },
  { key: 'coolingMinutes', label: texts.detail.cooling },
];

/** Formular zum Anlegen und Bearbeiten. */
export function RecipeEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editor = useRecipeEditor(id ?? null);
  const catalog = useCatalog();

  const draft = editor.draft;
  const noticeRef = useRef<HTMLDivElement | null>(null);
  const [searchParams] = useSearchParams();
  // Über das Teilen-Menü kommt die Adresse als url oder steckt im text.
  const sharedUrl = urlFromShared(searchParams.get('url') ?? searchParams.get('text'));
  const importedImage = useRef<string | null>(null);

  // Konflikt oder Fehler stehen oben; gespeichert wird unten. Deshalb den
  // Hinweis in den Blick holen, sobald er erscheint.
  useEffect(() => {
    if (editor.conflict !== null || editor.error !== null) {
      noticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      noticeRef.current?.focus();
    }
  }, [editor.conflict, editor.error]);

  /** Ändert ein Feld des Entwurfs. */
  function set(changes: Partial<RecipeDraft>) {
    editor.setDraft({ ...draft, ...changes });
  }

  /** Speichert und wechselt zur Detailansicht. */
  async function save(force = false) {
    const result = await editor.save(force);
    if (result.kind === 'saved') {
      // Bild der importierten Seite nachträglich übernehmen; das Rezept ist
      // auch ohne Bild vollständig.
      const image = importedImage.current;
      if (image !== null && id === undefined) {
        importedImage.current = null;
        await api.post(`/api/recipes/${result.id}/media-from-url`, { url: image }).catch(() => {
          window.alert(texts.webImport.imageFailed);
        });
      }
      void navigate(`/rezepte/${result.id}`);
    } else if (result.kind === 'queued') {
      window.alert(texts.sync.savedOffline);
      void navigate(result.id === null ? '/rezepte' : `/rezepte/${result.id}`);
    }
  }

  if (editor.loading) {
    return <p className="px-4 py-6 text-sm text-content-muted">{texts.common.loading}</p>;
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight">
          {id === undefined ? texts.editor.newTitle : texts.editor.editTitle}
        </h1>
      </header>

      {id === undefined ? (
        <WebImport
          initialUrl={sharedUrl}
          onImported={(result) => {
            editor.setDraft(result.draft);
            importedImage.current = result.imageUrl;
          }}
        />
      ) : null}

      <div ref={noticeRef} tabIndex={-1} className="scroll-mt-20 outline-none">
        {editor.conflict === null ? null : (
          <div
            role="alertdialog"
            aria-labelledby="conflict-title"
            className="mb-4 rounded-xl border border-accent bg-accent-soft p-4 text-sm"
          >
            <h2 id="conflict-title" className="font-semibold text-accent-strong">
              {texts.sync.conflictTitle}
            </h2>
            <p className="mt-1">{texts.sync.conflictText}</p>
            <p className="mt-1 text-xs text-content-muted">
              {new Date(editor.conflict.updatedAt).toLocaleString('de-DE')} · {editor.conflict.title}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void save(true)}
                className="rounded-lg bg-accent px-3 py-1.5 font-semibold text-on-accent"
              >
                {texts.sync.conflictMine}
              </button>
              <button
                type="button"
                onClick={editor.takeTheirs}
                className="rounded-lg border border-border-subtle bg-surface-raised px-3 py-1.5"
              >
                {texts.sync.conflictTheirs}
              </button>
            </div>
          </div>
        )}

        {editor.error === null ? null : (
          <p role="alert" className="mb-4 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">
            {editor.error}
          </p>
        )}
      </div>

      <section className="mb-4 rounded-card border border-border-subtle bg-surface-raised p-4">
        <h2 className="mb-3 font-semibold">{texts.editor.basics}</h2>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium">{texts.editor.titleLabel}</span>
          <input
            value={draft.title}
            onChange={(event) => set({ title: event.target.value })}
            required
            className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2"
          />
        </label>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium">{texts.editor.description}</span>
          <textarea
            value={draft.description}
            onChange={(event) => set({ description: event.target.value })}
            rows={2}
            className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2"
          />
        </label>

        <fieldset className="mb-3">
          <legend className="mb-1 text-sm font-medium">{texts.editor.difficulty}</legend>
          <div className="flex flex-wrap gap-2">
            {(['easy', 'medium', 'hard'] as const).map((difficulty) => (
              <button
                key={difficulty}
                type="button"
                onClick={() => set({ difficulty })}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  draft.difficulty === difficulty
                    ? 'border-accent bg-accent text-on-accent'
                    : 'border-border-subtle text-content-muted'
                }`}
              >
                {difficulty === 'easy' ? 'Leicht' : difficulty === 'medium' ? 'Mittel' : 'Schwer'}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-content-muted">
            <DifficultyBadge difficulty={draft.difficulty} withHint />
          </p>
        </fieldset>

        <div className="flex flex-wrap items-end gap-3 text-sm">
          <label>
            <span className="mb-1 block font-medium">{texts.editor.baseYield}</span>
            <input
              type="number"
              min={1}
              value={draft.baseAmount}
              onChange={(event) => set({ baseAmount: Number(event.target.value) || 1 })}
              className="w-24 rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
          </label>
          <label>
            <span className="mb-1 block font-medium">{texts.editor.unit}</span>
            <select
              value={draft.baseUnit}
              onChange={(event) => set({ baseUnit: event.target.value as RecipeDraft['baseUnit'] })}
              className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
            >
              <option value="portions">Portionen</option>
              <option value="persons">Personen</option>
              <option value="pieces">Stück</option>
              <option value="pan">Form</option>
            </select>
          </label>
          <label className="flex-1">
            <span className="mb-1 block font-medium">Zusatz</span>
            <input
              value={draft.baseUnitDetail}
              onChange={(event) => set({ baseUnitDetail: event.target.value })}
              placeholder="Springform 26 cm"
              className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
          </label>
        </div>
      </section>

      <section className="mb-4 rounded-card border border-border-subtle bg-surface-raised p-4">
        <h2 className="mb-3 font-semibold">{texts.editor.times}</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {TIME_FIELDS.map((field) => (
            <label key={field.key} className="text-sm">
              <span className="mb-1 block text-content-muted">{field.label}</span>
              <input
                type="number"
                min={0}
                value={draft.times[field.key] ?? 0}
                onChange={(event) =>
                  set({ times: { ...draft.times, [field.key]: Number(event.target.value) || 0 } })
                }
                className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
            </label>
          ))}
        </div>
      </section>

      <section className="mb-4 rounded-card border border-border-subtle bg-surface-raised p-4">
        <h2 className="mb-2 font-semibold">{texts.editor.equipment}</h2>
        <p className="mb-2 text-xs text-content-muted">{texts.editor.equipmentHint}</p>
        <input
          value={draft.equipment.join(', ')}
          onChange={(event) =>
            set({
              equipment: event.target.value
                .split(',')
                .map((entry) => entry.trim())
                .filter((entry) => entry !== ''),
            })
          }
          className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        />
      </section>

      <section className="mb-4 rounded-card border border-border-subtle bg-surface-raised p-4">
        <h2 className="mb-2 font-semibold">{texts.editor.categories}</h2>
        <div className="mb-4 flex flex-wrap gap-2">
          {catalog.categories.map((category) => {
            const active = draft.categoryIds.includes(category.id);
            return (
              <button
                key={category.id}
                type="button"
                onClick={() =>
                  set({
                    categoryIds: active
                      ? draft.categoryIds.filter((entry) => entry !== category.id)
                      : [...draft.categoryIds, category.id],
                  })
                }
                className={`rounded-full border px-3 py-1 text-xs ${
                  active
                    ? 'border-accent bg-accent text-on-accent'
                    : 'border-border-subtle text-content-muted'
                }`}
              >
                {category.name}
              </button>
            );
          })}
        </div>

        <h2 className="mb-2 font-semibold">{texts.editor.tags}</h2>
        <p className="mb-2 text-xs text-content-muted">{texts.editor.tagsHint}</p>
        <input
          value={draft.tagNames.join(', ')}
          onChange={(event) =>
            set({
              tagNames: event.target.value
                .split(',')
                .map((entry) => entry.trim())
                .filter((entry) => entry !== ''),
            })
          }
          list="bekannte-schlagwoerter"
          className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        />
        <datalist id="bekannte-schlagwoerter">
          {catalog.tags.map((tag) => (
            <option key={tag.id} value={tag.name} />
          ))}
        </datalist>
      </section>

      <div className="mb-4">
        <IngredientListEditor
          ingredients={draft.ingredients}
          onChange={(ingredients) => set({ ingredients })}
          onQuickAdd={(text) => editor.addIngredients(parseIngredientLines(text))}
        />
      </div>

      <div className="mb-4">
        <StepListEditor
          steps={draft.steps}
          ingredients={draft.ingredients}
          onChange={(steps) => set({ steps })}
        />
        <button
          type="button"
          onClick={editor.addStep}
          className="mt-3 rounded-xl border border-border-subtle px-3 py-2 text-sm"
        >
          {texts.editor.addStep}
        </button>
      </div>

      <div className="mb-4">
        <RecipeMediaEditor recipeId={id ?? null} media={editor.media} onChange={editor.setMedia} />
      </div>

      <div className="flex gap-2">
        <Link
          to={id === undefined ? '/rezepte' : `/rezepte/${id}`}
          className="rounded-xl border border-border-subtle px-4 py-2.5 text-sm"
        >
          {texts.editor.cancel}
        </Link>
        <button
          type="button"
          onClick={() => void save()}
          disabled={editor.saving || draft.title.trim() === ''}
          className="flex-1 rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent disabled:opacity-50"
        >
          {editor.saving ? texts.common.loading : texts.editor.save}
        </button>
      </div>
    </section>
  );
}
